import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTrendDown, faArrowTrendUp, faScaleBalanced } from '@fortawesome/free-solid-svg-icons';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import toast from 'react-hot-toast';
import { useConti } from '../../components/conti/ContiLayout';
import { IconaTonda, euro } from '../../components/conti/contiTheme';
import { getBilancio, messaggioErrore } from '../../services/contiService';

ChartJS.register(ArcElement, Tooltip, Legend);

const PALETTE = ['#3b5bdb', '#0ca678', '#f08c00', '#e03131', '#7048e8', '#1c7ed6', '#c2255c', '#2f9e44', '#e8590c', '#495057'];
const iso = (d) => d.toISOString().slice(0, 10);

const periodi = () => {
  const n = new Date();
  const primoMese = new Date(n.getFullYear(), n.getMonth(), 1);
  const ultimoMese = new Date(n.getFullYear(), n.getMonth() + 1, 0);
  const meseScorso1 = new Date(n.getFullYear(), n.getMonth() - 1, 1);
  const meseScorso2 = new Date(n.getFullYear(), n.getMonth(), 0);
  return {
    mese: { l: 'Questo mese', da: iso(primoMese), a: iso(ultimoMese) },
    scorso: { l: 'Mese scorso', da: iso(meseScorso1), a: iso(meseScorso2) },
    anno: { l: `Anno ${n.getFullYear()}`, da: `${n.getFullYear()}-01-01`, a: `${n.getFullYear()}-12-31` },
  };
};

const Bilancio = () => {
  const { scope, tema } = useConti();
  const P = useMemo(periodi, []);
  const [periodo, setPeriodo] = useState('mese');
  const [da, setDa] = useState(P.mese.da);
  const [a, setA] = useState(P.mese.a);
  const [dati, setDati] = useState(null);
  const [caricamento, setCaricamento] = useState(true);

  const scegli = (k) => { setPeriodo(k); if (P[k]) { setDa(P[k].da); setA(P[k].a); } };

  const carica = useCallback(async () => {
    if (!da || !a) return;
    setCaricamento(true);
    try {
      setDati(await getBilancio(scope, da, a));
    } catch (e) {
      toast.error(messaggioErrore(e, 'Impossibile calcolare il bilancio'));
      setDati(null);
    } finally {
      setCaricamento(false);
    }
  }, [scope, da, a]);

  useEffect(() => { carica(); }, [carica]);

  const spese = dati?.spese_per_voce || [];
  const entrate = dati?.entrate_per_voce || [];
  const colore = (v, i) => v.colore || PALETTE[i % PALETTE.length];
  const risultatoPositivo = (dati?.risultato || 0) >= 0;

  const scheda = (titolo, valore, icona, col) => (
    <Col md={4}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '18px 20px', boxShadow: '0 2px 10px rgba(0,0,0,.07)', borderLeft: `6px solid ${col}` }}>
        <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: 13 }}>
          <FontAwesomeIcon icon={icona} style={{ color: col }} />{titolo}
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color: col }}>{euro(valore)}</div>
      </div>
    </Col>
  );

  const elenco = (titolo, righe, col) => (
    <div style={{ background: '#fff', borderRadius: 16, padding: 18, boxShadow: '0 2px 10px rgba(0,0,0,.07)', height: '100%' }}>
      <h6 style={{ color: col, fontWeight: 700 }}>{titolo}</h6>
      {righe.length === 0 && <div className="text-muted py-3">Nessun dato nel periodo.</div>}
      {righe.map((v, i) => {
        const max = righe[0].importo || 1;
        return (
          <div key={v.nome} className="mb-2">
            <div className="d-flex align-items-center gap-2">
              <IconaTonda icona={v.icona} colore={colore(v, i)} size={30} />
              <div className="flex-grow-1 text-truncate">{v.nome}</div>
              <strong>{euro(v.importo)}</strong>
            </div>
            <div style={{ height: 6, background: '#f1f3f5', borderRadius: 4, marginTop: 4 }}>
              <div style={{ height: 6, width: `${Math.max(3, (v.importo / max) * 100)}%`, background: colore(v, i), borderRadius: 4 }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        {Object.entries(P).map(([k, v]) => (
          <button key={k} type="button" onClick={() => scegli(k)}
            style={{ border: 0, borderRadius: 999, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              background: periodo === k ? tema.colore : '#e9ecef', color: periodo === k ? '#fff' : '#495057' }}>{v.l}</button>
        ))}
        <button type="button" onClick={() => setPeriodo('custom')}
          style={{ border: 0, borderRadius: 999, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
            background: periodo === 'custom' ? tema.colore : '#e9ecef', color: periodo === 'custom' ? '#fff' : '#495057' }}>Personalizzato</button>
        {periodo === 'custom' && (
          <>
            <Form.Control type="date" value={da} onChange={(e) => setDa(e.target.value)} style={{ width: 160 }} />
            <span>→</span>
            <Form.Control type="date" value={a} onChange={(e) => setA(e.target.value)} style={{ width: 160 }} />
          </>
        )}
      </div>

      {caricamento && <div className="text-center py-5"><Spinner animation="border" /></div>}
      {!caricamento && !dati && <Alert variant="warning">Non riesco a calcolare il bilancio: controlla che Firefly sia raggiungibile.</Alert>}

      {!caricamento && dati && (
        <>
          <Row className="g-3 mb-3">
            {scheda('Entrate', dati.totale_entrate, faArrowTrendUp, '#2f9e44')}
            {scheda('Spese', dati.totale_spese, faArrowTrendDown, '#e03131')}
            {scheda('Risultato', dati.risultato, faScaleBalanced, risultatoPositivo ? tema.scuro : '#e03131')}
          </Row>
          <Row className="g-3">
            <Col lg={4}>
              <div style={{ background: '#fff', borderRadius: 16, padding: 18, boxShadow: '0 2px 10px rgba(0,0,0,.07)', height: '100%' }}>
                <h6 style={{ color: tema.scuro, fontWeight: 700 }}>Come si distribuiscono le spese</h6>
                {spese.length === 0 ? <div className="text-muted py-5 text-center">Nessuna spesa nel periodo.</div> : (
                  <Doughnut
                    data={{ labels: spese.map((v) => v.nome), datasets: [{ data: spese.map((v) => v.importo), backgroundColor: spese.map(colore), borderWidth: 2 }] }}
                    options={{ plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } }, cutout: '62%' }}
                  />
                )}
              </div>
            </Col>
            <Col lg={4}>{elenco('Spese per voce', spese, '#e03131')}</Col>
            <Col lg={4}>{elenco('Entrate per voce', entrate, '#2f9e44')}</Col>
          </Row>
          {scope === 'personale' && dati.spese_per_persona?.length > 0 && (
            <Row className="g-3 mt-1">
              {dati.spese_per_persona.map((p) => (
                <Col key={p.persona} md={4}>
                  <div style={{ background: tema.morbido, borderRadius: 14, padding: '14px 18px' }}>
                    <div className="text-muted" style={{ fontSize: 13 }}>Spese {p.persona}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: tema.scuro }}>{euro(p.importo)}</div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
          <div className="text-muted mt-3" style={{ fontSize: 12 }}>
            Il bilancio considera i movimenti registrati su Firefly con una voce del piano dei conti.
            I movimenti più vecchi senza voce compaiono come «(senza voce)».
          </div>
        </>
      )}
    </>
  );
};

export default Bilancio;
